import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { toast, ImpersonationBanner as SharedBanner } from '@clareefai/ui';
import { auth } from '@clareefai/api/firebase';
import { getImpersonationExpiresAt } from '@clareefai/api/impersonation';
import {
  extendImpersonationSession,
  useImpersonationTabTitle,
} from '@clareefai/api/impersonation-hooks';
import { useAuth } from '../../contexts/AuthContext';

const ImpersonationBanner: React.FC = () => {
  const { isImpersonating, impersonationSessionId, user, exitImpersonation } = useAuth();
  const [isExtending, setIsExtending] = useState(false);
  const [expiresAt, setExpiresAt] = useState<Date | null>(() => getImpersonationExpiresAt());

  useEffect(() => {
    setExpiresAt(getImpersonationExpiresAt());
  }, [isImpersonating, impersonationSessionId]);

  useImpersonationTabTitle(isImpersonating, user?.name || user?.username);

  useEffect(() => {
    if (!isImpersonating) return;
    Sentry.setTag('impersonation.active', 'true');
    Sentry.setTag('impersonation.session_id', impersonationSessionId ?? 'unknown');
    Sentry.setContext('impersonation', {
      sessionId: impersonationSessionId,
      targetName: user?.name || user?.username,
      targetEmail: user?.username,
      expiresAt: expiresAt?.toISOString(),
    });
    return () => {
      Sentry.setTag('impersonation.active', 'false');
      Sentry.setContext('impersonation', null);
    };
  }, [isImpersonating, impersonationSessionId, user, expiresAt]);

  const handleExtend = async () => {
    setIsExtending(true);
    try {
      const token = (await auth.currentUser?.getIdToken()) ?? null;
      const result = await extendImpersonationSession(
        import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api',
        token
      );
      if (result) {
        setExpiresAt(new Date(result.expiresAt));
        toast.success('Session extended by 30 minutes');
      } else {
        toast.error('Could not extend session. Maximum duration may have been reached.');
      }
    } finally {
      setIsExtending(false);
    }
  };

  return (
    <SharedBanner
      active={isImpersonating}
      displayName={user?.name || user?.username}
      displayEmail={user?.username}
      expiresAt={expiresAt}
      onExit={exitImpersonation}
      onExtend={handleExtend}
      isExtending={isExtending}
    />
  );
};

export default ImpersonationBanner;
